import {
  TransactionGetCart,
  TransactionPostOrders,
} from "../Services/Transactions";
import { EmptyCart } from "../Components/Pages";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { LoadingContext } from "../Context";
import { CartDetailType, CartType } from "../Structures";
import { Button } from "../Components/Base";
import { ConfirmModal, Modal } from "../Components/Modal";
import { formatDate, formatPrice, Toast } from "../Utils";
import { TransactionDeleteCart } from "../Services/Transactions";

const Cart = () => {
  const { setLoading } = useContext(LoadingContext);

  // ==============================================================

  const [selectedForDelete, setSelectedForDelete] = useState<number>(0);
  const [details, setDetails] = useState<CartType>({ cart_details: [] });
  const [selectedDetails, setSelectedDetails] = useState<CartDetailType[]>([]);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // ======================================================================

  const getTotalPrice = () => {
    return selectedDetails.reduce((acc, item) => {
      acc += item.product.price;
      return acc;
    }, 0);
  };

  const handlePayment = () => {
    if (screenshot) {
      setIsLoading(true);
      const form = new FormData();
      form.append("screen_shot", screenshot);
      form.append(
        "cart_detail_id_list",
        JSON.stringify(selectedDetails.map((item) => item.id))
      );
      TransactionPostOrders(form)
        .then(() => {
          let temp = [...details.cart_details];

          selectedDetails.forEach((item) => {
            temp = temp.filter((c) => c.id != item.id);
          });

          setDetails({ cart_details: temp });
          setSelectedDetails([]);

          setShowPaymentModal(false);
          Toast("success", "رسید شما با موفقیت ذخیره شد.");
        })
        .finally(() => setIsLoading(false));
    } else {
      Toast("error", "لطفا ابتدا فیش واریزی را آپلود کنید.");
    }
  };

  const handleDeleteItem = () => {
    setLoading(true);
    setSelectedForDelete(0);
    TransactionDeleteCart(selectedForDelete)
      .then(({ data: res }) => {
        if (res.transaction_status) {
          Toast("success", "محصول با موفقیت حذف شد.");
          setDetails({
            cart_details: details.cart_details.filter(
              (item) => item.id != selectedForDelete
            ),
          });
          setSelectedDetails(
            selectedDetails.filter((item) => item.id != selectedForDelete)
          );
        } else {
          Toast("error", res.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleSelectDetail = (detail: CartDetailType) => {
    const target = selectedDetails.find((item) => item.id == detail.id);
    if (target) {
      setSelectedDetails(
        selectedDetails.filter((item) => item.id != detail.id)
      );
    } else {
      setSelectedDetails([...selectedDetails, detail]);
    }
  };

  // ======================================================================

  useEffect(() => {
    setLoading(true);
    TransactionGetCart()
      .then(({ data: res }) => {
        if (res.transaction_status) {
          setDetails({ cart_details: res.data!.cart_details.reverse() });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // ======================================================================

  return details.cart_details.length ? (
    <section className="pt-3 pt-lg-5">
      <div className="container">
        <div className="row g-4 g-lg-5">
          <div className="col-lg-8">
            <div className="card bg-transparent">
              <div className="card-header border-bottom d-flex justify-content-between align-items-center px-0 pb-3">
                <h4 className="card-title mb-0">سفارشات</h4>
                <span className="bg-primary px-3 py-1 text-white fw-bold rounded-2">
                  مجموع سفارشات: {details.cart_details.length}
                </span>
              </div>

              <div className="card-body p-0 pt-4">
                {details.cart_details.map((item) => (
                  <Fragment key={item.id}>
                    <div className="d-flex align-items-center">
                      <div
                        onClick={() => handleSelectDetail(item)}
                        className={`selectbox ${
                          selectedDetails.map((d) => d.id).includes(item.id)
                            ? "bg-primary"
                            : ""
                        }`}
                      >
                        <i className="text-white bi bi-check fs-5 mt-1" />
                      </div>

                      <div className="col-5 col-md-2">
                        <div
                          style={{ minHeight: 100 }}
                          className="bg-light d-flex justify-content-center align-items-center overflow-hidden border rounded-2"
                        >
                          <img
                            src={item.product.thumbnail}
                            alt={item.product.title}
                          />
                        </div>
                      </div>

                      <div className="flex-grow-1">
                        <div
                          className="d-flex flex-md-row flex-column align-items-start align-items-md-center px-3"
                          style={{ gap: 8 }}
                        >
                          <div className="col-md-7">
                            <h5 className="mb-1">{item.product.title}</h5>

                            <ul className="nav nav-divider small align-items-center mt-1">
                              <li className="nav-item">
                                تاریخ: {formatDate(item.create_time)}
                              </li>
                            </ul>
                          </div>

                          <div className="col-md-3 flex-grow-1 text-md-end">
                            <h5>{formatPrice(item.product.price)} تومان</h5>

                            <div className="hstack gap-1 flex-wrap text-primary-hover mt-2 justify-content-md-end">
                              <span
                                className="cursor-pointer text-danger px-3 py-1 rounded-2  bg-danger-subtle small"
                                onClick={() => setSelectedForDelete(item.id)}
                              >
                                <i className="bi text-danger bi-trash3 me-1"></i>
                                حذف
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <hr />
                  </Fragment>
                ))}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card overflow-hidden bg-light border border-secondary border-opacity-25 rounded-2 p-4">
              {selectedDetails.length ? (
                <Fragment>
                  <div className="card-header bg-light p-0 pb-3">
                    <h5 className="card-title mb-0">جزئیات پرداخت</h5>
                  </div>

                  <div className="card-body p-0 pb-3">
                    <ul className="list-group list-group-borderless">
                      {selectedDetails.map((item) => (
                        <li
                          key={item.id}
                          className="list-group-item factor-item d-flex justify-content-between align-items-center"
                        >
                          <span
                            className="truncate flex-grow-1 flex-shrink-1"
                            style={{ flexBasis: 0 }}
                          >
                            {item.product.title}
                          </span>
                          <span className="h6 mb-0">
                            {formatPrice(item.product.price)} تومان
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="card-body px-0 py-3 border-top">
                    <ul className="list-group list-group-borderless">
                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        <span>مجموع:</span>
                        <span className="h6 mb-0">
                          {formatPrice(getTotalPrice())} تومان
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="card-footer bg-light p-0">
                    <Button
                      text="پرداخت"
                      onClick={() => setShowPaymentModal(true)}
                      btnClass="w-100 btn-primary"
                    />
                  </div>
                </Fragment>
              ) : (
                <div className="no-selected-cart-detail-wrapper">
                  <h5>حداقل یک سفارش را برای پرداخت انتخاب کنید</h5>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedForDelete ? (
        <ConfirmModal
          onClose={() => setSelectedForDelete(0)}
          onSubmit={handleDeleteItem}
        />
      ) : null}

      {showPaymentModal ? (
        <Modal close={() => setShowPaymentModal(false)}>
          <div
            className="bg-white rounded-3 flex-column z-index-10 overflow-hidden d-flex justify-content-center align-items-center"
            style={{ width: 600, height: 400 }}
          >
            <div className="w-100 h-100 pb-0 p-4 overflow-hidden rounded-t-3 d-flex justify-content-center align-items-center">
              <input
                id="screenshot_input"
                type="file"
                hidden
                accept=".png,.jpg,.jpeg"
                onChange={(e) =>
                  setScreenshot(e.target.files ? e.target.files[0] : null)
                }
              />
              <label
                htmlFor="screenshot_input"
                className="bg-white cursor-pointer border border-info rounded-3 d-flex justify-content-center gap-3 flex-column align-items-center w-100 h-100"
              >
                {screenshot ? (
                  <img
                    src={URL.createObjectURL(screenshot)}
                    alt={screenshot.name}
                    className="w-100 h-100 object-fit-contain rounded-3"
                  />
                ) : (
                  <>
                    <i
                      className="fa fa-cloud-upload-alt text-primary"
                      style={{ fontSize: 60 }}
                    />
                    <h4>برای آپلود عکس کلیک کنید.</h4>
                  </>
                )}
              </label>
            </div>
            <hr />
            <div className="bg-white pb-1 p-3 d-flex w-100 border-top border-info justify-content-end align-items-center gap-2">
              <Button
                btnClass="text-primary cursor-pointer border border-primary  w-25"
                text="انصراف"
                onClick={() => setShowPaymentModal(false)}
              />
              <Button
                onClick={handlePayment}
                btnClass="btn-primary cursor-pointer w-25"
                text="تایید"
                isLoading={isLoading}
              />
            </div>
          </div>
        </Modal>
      ) : null}
    </section>
  ) : (
    <EmptyCart />
  );
};

export default Cart;
